/**
 * Name Blacklist settings component
 * Manage patterns to exclude places by name (case-insensitive substring match)
 */

import { useState } from 'react';
import { Input, Button, Table, Alert, Space, Popconfirm, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, StopOutlined } from '@ant-design/icons';
import { useNameBlacklist, useAddNameBlacklistEntry, useRemoveNameBlacklistEntry } from '@/hooks/useSettings';
import type { NameBlacklistEntry } from '@/services/systemApi';

const { Title } = Typography;

export function NameBlacklist() {
  const [newPattern, setNewPattern] = useState('');
  const { data: entries = [], isLoading } = useNameBlacklist();
  const addEntry = useAddNameBlacklistEntry();
  const removeEntry = useRemoveNameBlacklistEntry();

  const handleAdd = async () => {
    const trimmed = newPattern.trim();
    if (!trimmed) return;
    await addEntry.mutateAsync(trimmed);
    setNewPattern('');
  };

  const columns = [
    {
      title: 'Pattern',
      dataIndex: 'pattern',
      key: 'pattern',
    },
    {
      title: 'Added',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (val: string) => new Date(val).toLocaleDateString(),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_: unknown, record: NameBlacklistEntry) => (
        <Popconfirm
          title="Remove this pattern?"
          onConfirm={() => removeEntry.mutate(record.id)}
          okText="Remove"
          cancelText="Cancel"
        >
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            loading={removeEntry.isPending && removeEntry.variables === record.id}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Title level={4}>
        <StopOutlined style={{ marginRight: 8 }} />
        Name Blacklist
      </Title>

      <Alert
        message="Places whose name contains any of these patterns (case-insensitive) will be hidden from Results and Maps."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Space.Compact style={{ width: '100%', maxWidth: 500, marginBottom: 16 }}>
        <Input
          placeholder="Enter pattern (e.g. McDonald's)"
          value={newPattern}
          onChange={(e) => setNewPattern(e.target.value)}
          onPressEnter={handleAdd}
          maxLength={255}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          loading={addEntry.isPending}
          disabled={!newPattern.trim()}
        >
          Add
        </Button>
      </Space.Compact>

      <Table
        dataSource={entries}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        size="small"
        pagination={entries.length > 20 ? { pageSize: 20 } : false}
        locale={{ emptyText: 'No blacklist patterns configured' }}
      />
    </div>
  );
}

export default NameBlacklist;
